import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuery } from '@tanstack/react-query';
import {
  PaymentSchema,
  SupplierInvoiceSchema,
  SupplierPayableBalanceSchema,
  SupplierSchema,
} from '@taqon/contracts';
import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router';
import { getData, getPage } from '../../shared/api/client';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { MigrationTrace } from '../migration/MigrationTrace';
import { Facts, RecordFailure, RecordLink } from '../finance/detail';
import { categoryLabel, isMasked, SupplierDialog, useSupplierCategories } from './SuppliersPage';
import { SupplierPurchasing } from './SupplierPurchasing';
import { EmailLink, PhoneLinks } from '../../shared/ui/contact';

/**
 * One supplier (`/suppliers/:id`): the master record — the TRN and bank details as the masking
 * path returned them, masked unless the session holds the unmask grant (FRD-PRC-010, FRD-USR-016)
 * — with, where the session may read them, the outstanding payable and the supplier's invoices and
 * payments, each linked (FRD-FIN-077). Editing is offered only with the change permission.
 */
export function SupplierDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const canChange = hasCapability(session.data, 'procurement.supplier:change');
  const [editing, setEditing] = useState(false);
  const categories = useSupplierCategories();
  const supplier = useQuery({
    queryKey: ['supplier', id],
    queryFn: () => getData(`/api/v1/procurement/suppliers/${id}`, SupplierSchema),
  });
  const back = (
    <Button component={RouterLink} to="/suppliers" startIcon={<ArrowBackIcon />}>
      All suppliers
    </Button>
  );
  if (supplier.isPending) return <LoadingState label="Loading the supplier" />;
  if (supplier.isError) {
    return <RecordFailure title="Supplier" noun="supplier" error={supplier.error} back={back} />;
  }
  const s = supplier.data;
  // A masked value is shown exactly as the masking path rendered it, and said to be masked.
  const shown = (value: string | null) =>
    value === null ? '—' : isMasked(value) ? `${value} (masked)` : value;
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`${s.code} — ${s.companyName}`}
        actions={
          <>
            {back}
            {canChange && (
              <Button
                variant="contained"
                onClick={(event) => {
                  // The dialog returns focus here when it closes.
                  event.currentTarget.focus();
                  setEditing(true);
                }}
              >
                Edit
              </Button>
            )}
          </>
        }
      />
      <Section title="Supplier">
        <Facts
          label="Supplier details"
          facts={[
            ['Status', <StatusChip key="s" status={s.status} />],
            ['Company name', s.companyName],
            ['Category', categoryLabel(categories.data ?? [], s.categoryCode)],
            ['Contact name', s.contactName ?? '—'],
            ['Contact email', <EmailLink key="e" email={s.contactEmail} />],
            ['Contact phone', <PhoneLinks key="p" phone={s.contactPhone} />],
            ['Address', [s.addressLine1, s.city, s.country].filter(Boolean).join(', ') || '—'],
            ['TRN', shown(s.trn)],
            ['Bank name', shown(s.bankName)],
            ['Bank account name', shown(s.bankAccountName)],
            ['Bank account number', shown(s.bankAccountNumber)],
          ]}
        />
      </Section>
      {/* Supplier compliance documents (FRD-PRC-011). */}
      <DocumentsPanel
        recordType="procurement.supplier"
        recordId={s.id}
        canAttach={canChange}
        title="Compliance documents"
      />
      <Payables supplierId={s.id} />
      <SupplierPurchasing supplierId={s.id} />
      <MigrationTrace recordType="procurement.supplier" recordId={s.id} />
      <RecordHistory recordType="procurement.supplier" recordId={s.id} />
      {editing && (
        <SupplierDialog
          key={String(s.version)}
          supplier={s}
          onClose={() => {
            setEditing(false);
          }}
        />
      )}
    </Stack>
  );
}

function Payables({ supplierId }: { supplierId: string }) {
  const session = useSession();
  const canSeeInvoices = hasCapability(session.data, 'finance.supplier_invoice:view');
  const canSeePayments = hasCapability(session.data, 'finance.payment:view');
  const balance = useQuery({
    queryKey: ['payable-balance', supplierId],
    enabled: canSeeInvoices,
    queryFn: () =>
      getData(
        `/api/v1/finance/suppliers/${supplierId}/payable-balance`,
        SupplierPayableBalanceSchema,
      ),
  });
  const invoices = useQuery({
    queryKey: ['supplier-invoices', 'of-supplier', supplierId],
    enabled: canSeeInvoices,
    queryFn: () =>
      getPage(
        `/api/v1/finance/supplier-invoices?pageSize=50&sort=-document_no&filter[supplier_id]=${supplierId}`,
        SupplierInvoiceSchema,
      ),
  });
  const payments = useQuery({
    queryKey: ['payments', 'of-supplier', supplierId],
    enabled: canSeePayments,
    queryFn: () =>
      getPage(
        `/api/v1/finance/payments?pageSize=50&sort=-document_no&filter[supplier_id]=${supplierId}`,
        PaymentSchema,
      ),
  });
  return (
    <>
      {canSeeInvoices && (
        <Section title="Invoices">
          <Stack sx={{ p: 2 }}>
            Outstanding payable: {balance.data?.outstandingAmount ?? '…'} across{' '}
            {balance.data?.invoiceCount ?? '…'} posted invoice(s).
          </Stack>
          {invoices.isPending && <LoadingState label="Loading invoices" />}
          {invoices.isError && <ErrorState message={invoices.error.message} />}
          {invoices.data?.items.length === 0 && <EmptyState title="No invoices" />}
          {invoices.data !== undefined && invoices.data.items.length > 0 && (
            <Table size="small" aria-label="Invoices of this supplier">
              <TableHead>
                <TableRow>
                  <TableCell>Document</TableCell>
                  <TableCell>Supplier reference</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Gross</TableCell>
                  <TableCell align="right">Outstanding</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.data.items.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`/supplier-invoices/${invoice.id}`}>
                        {invoice.documentNo}
                      </RecordLink>
                    </TableCell>
                    <TableCell>{invoice.supplierInvoiceNumber}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {invoice.supplierInvoiceDate}
                    </TableCell>
                    <TableCell align="right">{invoice.grossTotal}</TableCell>
                    <TableCell align="right">{invoice.outstandingAmount}</TableCell>
                    <TableCell>
                      <StatusChip status={invoice.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      )}
      {canSeePayments && (
        <Section title="Payments">
          {payments.isPending && <LoadingState label="Loading payments" />}
          {payments.isError && <ErrorState message={payments.error.message} />}
          {payments.data?.items.length === 0 && <EmptyState title="No payments" />}
          {payments.data !== undefined && payments.data.items.length > 0 && (
            <Table size="small" aria-label="Payments to this supplier">
              <TableHead>
                <TableRow>
                  <TableCell>Document</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.data.items.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`/payments/${payment.id}`}>{payment.documentNo}</RecordLink>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{payment.paymentDate}</TableCell>
                    <TableCell align="right">{payment.amount}</TableCell>
                    <TableCell>
                      <StatusChip status={payment.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      )}
    </>
  );
}
